import styles from '../index.module.css'

import React from 'react'

import type { NativeTag } from 'types/track'

import { Flex, Text, Tooltip } from 'components/ds'

import { resolveIndicatorColor } from '../utils/indicator-colors'

type Props = {
  tag: NativeTag
  isActive?: boolean
  zIndex?: number
  onClick?: (event: React.MouseEvent, tagId: number) => void
}

export const TagDot = ({
  tag,
  isActive = false,
  zIndex = 1,
  onClick,
}: Props): React.ReactElement | null => {
  const { color, name } = tag
  const background = resolveIndicatorColor(color)

  // Callers filter with getColoredTags, so an unresolved color is bad data.
  if (!background) return null

  // Hidden from screen readers: the tag name sits beside it as text.
  const dot = (
    <span
      className={styles.dot}
      aria-hidden
      data-testid="indicator-swatch"
      // Palette hex comes from tag data, so it cannot live in the CSS module.
      style={{ background, zIndex }}
    />
  )

  if (!onClick) return dot

  return (
    <Tooltip
      content={
        <Flex direction="column" gap="1" pb="1">
          <Text weight="medium">
            {isActive ? 'Clear filter:' : 'Filter by:'}
          </Text>
          <Flex as="span" gap="1" align="center" className={styles.tooltipTag}>
            {dot}
            <Text weight="medium">{name}</Text>
          </Flex>
        </Flex>
      }
    >
      {/* Fixed name, state via aria-pressed: no rename on every toggle. */}
      <button
        type="button"
        className={styles.dot}
        aria-label={`Filter by ${name}`}
        aria-pressed={isActive}
        data-testid="indicator-dot"
        onClick={event => onClick(event, tag.id)}
        style={{ background, zIndex }}
      />
    </Tooltip>
  )
}
